import { useCallback, useEffect, useRef, useState } from "react";

/**
 * useRouletteSnippets — question consumption for the roulette board.
 *
 * The SERVER builds one shuffled, duplicate-free `sequence` per session
 * (routes/_deck.py) and persists it in `play_sessions`. The client receives
 * a copy and consumes it STRICTLY IN ORDER, synchronously, inside
 * `nextRound()` — which GamePlay.animateSpin calls exactly once per round.
 *
 * Guarantees (all modes — solo, competition, Grande Final, VS Robot,
 * Eliminatórias; VS Humano uses the server round index via paintVshRound):
 *   1. No reshuffle / no re-draw: the next question is always the FIRST
 *      unserved entry in server order for the drawn roulette letter.
 *   2. A question only repeats after EVERY eligible question of the
 *      sequence was served in this session (cycle restart), and never
 *      right after itself.
 *   3. Consumption never depends on a useEffect observing a render
 *      transition (previous root cause of same-question-next-round).
 */
export function useRouletteSnippets({ session, mode, sequenceRef }) {
  const [displayedByPos, setDisplayedByPos] = useState({});
  const [snippetsByPos, setSnippetsByPos] = useState({});
  const displayedRef = useRef({});

  // Card mode (decks imported with Excel column H).
  const cardsRef = useRef([]);
  const playedRef = useRef(new Set());
  const lastCardKeyRef = useRef(null);
  const pendingLandingRef = useRef(null);
  const soloRoundsRef = useRef([]);
  const soloCursorRef = useRef(0);
  const roundsRef = useRef([]);
  const lastQuestionTextRef = useRef("");

  // Sequence mode (all real decks): server order, served bookkeeping.
  const queueRef = useRef([]);
  const servedIdsRef = useRef(new Set());
  const servedTextsRef = useRef(new Set());
  const poolsRef = useRef({});

  const seededSessionRef = useRef(null);

  const shuffle = (arr) => {
    const a = arr.slice();
    for (let i = a.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  };

  const normText = (it) => (it?.question || "").toString().trim().toLowerCase();

  const computeSnippets = useCallback((display) => {
    const out = {};
    for (const pos of Object.keys(display || {})) {
      const front = display[pos];
      const opts = (front?.options || [])
        .slice(0, 3)
        .map((o) => (o || "").toString().trim())
        .filter(Boolean);
      out[pos] = opts.length > 0 ? opts[Math.floor(Math.random() * opts.length)] : "";
    }
    return out;
  }, []);

  const commitDisplay = useCallback((display) => {
    displayedRef.current = display;
    setDisplayedByPos(display);
    setSnippetsByPos(computeSnippets(display));
  }, [computeSnippets]);

  // ---------------- Card mode helpers ----------------
  const pickLanding = (card, pool) => {
    if (!card) return null;
    const positions = Array.isArray(card.positions) && card.positions.length > 0
      ? card.positions
      : Object.keys(card.questions || {});
    if (positions.length === 0) return null;
    const eligible = Array.isArray(pool) ? pool.filter((p) => positions.includes(p)) : [];
    const source = eligible.length > 0 ? eligible : positions;
    const pos = source[Math.floor(Math.random() * source.length)];
    return card.questions?.[pos] || null;
  };

  const serveNextCard = () => {
    const deck = cardsRef.current;
    if (!deck.length) return null;
    let idx = deck.findIndex((c) => !playedRef.current.has(c._key));
    if (idx === -1) {
      const re = shuffle(deck);
      if (re.length > 1 && re[0]._key === lastCardKeyRef.current) {
        const j = 1 + Math.floor(Math.random() * (re.length - 1));
        [re[0], re[j]] = [re[j], re[0]];
      }
      cardsRef.current = re;
      playedRef.current = new Set();
      idx = 0;
    }
    let card = cardsRef.current[idx];
    if (card._key === lastCardKeyRef.current && cardsRef.current.length > 1) {
      const altIdx = cardsRef.current.findIndex((c, i) => i !== idx && !playedRef.current.has(c._key));
      if (altIdx !== -1) card = cardsRef.current[altIdx];
    }
    playedRef.current.add(card._key);
    lastCardKeyRef.current = card._key;
    return card;
  };

  // ---------------- Sequence mode helpers ----------------
  const isFree = (it) => !!it && !servedIdsRef.current.has(it.id) && !servedTextsRef.current.has(normText(it));

  const firstFree = (pos, avoidText) =>
    queueRef.current.find(
      (it) => (pos == null || it.position === pos) && isFree(it) && (!avoidText || normText(it) !== avoidText),
    ) || null;

  // Strict ordered take — mirrors backend routes/_deck.py:pick_next.
  const takeNextFromSequence = (pool) => {
    const q = queueRef.current;
    if (!q.length) return null;
    let order = [null];
    if (Array.isArray(pool) && pool.length > 0) {
      const want = pool[Math.floor(Math.random() * pool.length)];
      const others = shuffle([...new Set(pool)].filter((p) => p !== want));
      order = [want, ...others];
      const poolSet = new Set(pool);
      if (!q.some((it) => poolSet.has(it.position))) order = [null];
    }
    const pick = (avoidText) => {
      for (const pos of order) {
        const it = firstFree(pos, avoidText);
        if (it) return it;
      }
      return null;
    };
    let item = pick("");
    if (!item) {
      // Every eligible question served → new cycle; never repeat the last one immediately.
      servedIdsRef.current = new Set();
      servedTextsRef.current = new Set();
      item = pick(lastQuestionTextRef.current) || pick("");
    }
    if (!item) return null;
    servedIdsRef.current.add(item.id);
    servedTextsRef.current.add(normText(item));
    lastQuestionTextRef.current = normText(item);
    return item;
  };

  // Paint the landing question on its own letter; other letters get a
  // cosmetic (unserved-preferred) question so the board feels alive.
  const paintLanding = useCallback((item) => {
    const next = { ...displayedRef.current };
    next[item.position] = item;
    for (const pos of Object.keys(poolsRef.current)) {
      if (pos === item.position) continue;
      const pool = poolsRef.current[pos] || [];
      if (pool.length <= 1) { if (!next[pos] && pool[0]) next[pos] = pool[0]; continue; }
      const cur = next[pos];
      const fresh = pool.filter((qq) => isFree(qq) && (!cur || qq.id !== cur.id));
      const alt = fresh.length > 0 ? fresh : pool.filter((qq) => !cur || qq.id !== cur.id);
      const pickArr = alt.length > 0 ? alt : pool;
      next[pos] = pickArr[Math.floor(Math.random() * pickArr.length)];
    }
    commitDisplay(next);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commitDisplay]);

  // ---------------- Seed on session load ----------------
  const sessionId = session?.session_id || null;
  useEffect(() => {
    if (!session) return;
    if (seededSessionRef.current === sessionId) return;
    seededSessionRef.current = sessionId;
    servedIdsRef.current = new Set();
    servedTextsRef.current = new Set();
    lastQuestionTextRef.current = "";
    pendingLandingRef.current = null;
    if (Array.isArray(session.cards) && session.cards.length > 0) {
      cardsRef.current = session.cards.map((c, i) => ({
        ...c,
        _key: c.card_number != null ? `c${c.card_number}` : `s${i}`,
      }));
      roundsRef.current = Array.isArray(session.rounds) ? session.rounds : [];
      playedRef.current = new Set();
      lastCardKeyRef.current = null;
      soloRoundsRef.current = mode !== "vs_human" && Array.isArray(session.rounds) ? session.rounds : [];
      soloCursorRef.current = 0;
      queueRef.current = [];
      poolsRef.current = {};
      let firstIdx = 0;
      if (mode === "vs_human" && roundsRef.current.length > 0) {
        firstIdx = roundsRef.current[0].card_index || 0;
      } else if (soloRoundsRef.current.length > 0) {
        firstIdx = soloRoundsRef.current[0].card_index || 0;
      }
      const first = cardsRef.current[firstIdx] || cardsRef.current[0];
      if (first) commitDisplay({ ...(first.questions || {}) });
    } else {
      const pools = {};
      const display = {};
      for (const [pos, list] of Object.entries(session.questions || {})) {
        const arr = Array.isArray(list) ? list : (list ? [list] : []);
        if (arr.length === 0) continue;
        pools[pos] = arr.slice();
        display[pos] = arr[0];
      }
      // Server-generated order. Only if the server shipped nothing do we
      // build a local one-off shuffle (never re-shuffled afterwards).
      let seq = Array.isArray(session.sequence) ? session.sequence.slice() : [];
      if (seq.length === 0) {
        const flat = [];
        for (const [pos, list] of Object.entries(session.questions || {})) {
          const arr = Array.isArray(list) ? list : (list ? [list] : []);
          for (const q of arr) flat.push({ ...q, position: pos });
        }
        seq = shuffle(flat);
      }
      poolsRef.current = pools;
      queueRef.current = seq;
      cardsRef.current = [];
      roundsRef.current = [];
      soloRoundsRef.current = [];
      commitDisplay(display);
    }
  }, [sessionId, commitDisplay, session, mode]);

  // ---------------- Public API ----------------
  // nextRound(pool): called EXACTLY ONCE per round by GamePlay.animateSpin.
  // Synchronously consumes + paints the next question and returns it.
  const nextRound = useCallback((pool) => {
    if (cardsRef.current.length > 0) {
      if (soloRoundsRef.current.length > 0) {
        const lastText = lastQuestionTextRef.current;
        const total = soloRoundsRef.current.length;
        let picked = null;
        let pickedIdx = soloCursorRef.current;
        for (let probe = 0; probe < total; probe += 1) {
          const idx = (soloCursorRef.current + probe) % total;
          const r = soloRoundsRef.current[idx];
          const card = cardsRef.current[r.card_index];
          if (!card) continue;
          const lText = normText(card.questions?.[r.position]);
          if (!lastText || lText !== lastText) { picked = r; pickedIdx = idx; break; }
        }
        if (!picked) {
          pickedIdx = soloCursorRef.current % total;
          picked = soloRoundsRef.current[pickedIdx];
        }
        soloCursorRef.current = pickedIdx + 1;
        const card = cardsRef.current[picked.card_index];
        if (!card) return null;
        commitDisplay({ ...(card.questions || {}) });
        const positions = Object.keys(card.questions || {});
        const eligible = Array.isArray(pool) ? pool.filter((p) => positions.includes(p)) : [];
        const pos = eligible.length > 0 ? eligible[Math.floor(Math.random() * eligible.length)] : picked.position;
        const landing = card.questions?.[pos] || null;
        pendingLandingRef.current = landing;
        lastCardKeyRef.current = card._key;
        if (landing) lastQuestionTextRef.current = normText(landing);
        return landing;
      }
      const card = serveNextCard();
      if (!card) return null;
      commitDisplay({ ...(card.questions || {}) });
      pendingLandingRef.current = pickLanding(card, pool);
      return pendingLandingRef.current;
    }
    const item = takeNextFromSequence(pool);
    if (!item) return null;
    paintLanding(item);
    pendingLandingRef.current = item;
    return item;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commitDisplay, paintLanding]);

  // paintVshRound (VS HUMANO): both peers paint round `idx` from the
  // server's pre-baked list — card mode via rounds[], sequence mode via
  // sequence[idx]. Synchronous, index-driven, never re-drawn.
  const paintVshRound = useCallback((idx) => {
    const deck = cardsRef.current;
    if (deck.length > 0) {
      const rounds = roundsRef.current;
      if (!rounds.length) return null;
      const r = rounds[((idx % rounds.length) + rounds.length) % rounds.length];
      const card = deck[r.card_index];
      if (!card) return null;
      commitDisplay({ ...(card.questions || {}) });
      const landing = card.questions?.[r.position] || null;
      pendingLandingRef.current = landing;
      lastCardKeyRef.current = card._key;
      return landing;
    }
    const seq = sequenceRef?.current || [];
    if (!seq.length) return null;
    const item = seq[((idx % seq.length) + seq.length) % seq.length];
    if (!item || !item.position) return null;
    servedIdsRef.current.add(item.id);
    servedTextsRef.current.add(normText(item));
    paintLanding(item);
    pendingLandingRef.current = item;
    return item;
  }, [commitDisplay, paintLanding, sequenceRef]);

  const hasNext = useCallback(() => queueRef.current.length > 0 || cardsRef.current.length > 0, []);

  return { displayedByPos, snippetsByPos, nextRound, paintVshRound, hasNext };
}
